// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useMemo, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { Text } from '@/components/Themed';
import RoundGameInfoModal from '@/components/RoundGameInfoModal';
import { useUser } from '@/app/user-context';
import { getGameDisplayName } from '@/features/game-definitions/i18n';
import {
  formatMeritLadder,
  getDisplayMeritLadder,
  isAchievementMeritGame,
  isHazardMeritGame,
  isPerEventMeritGame,
  useGameScoringRules,
} from '@/features/game-definitions/merits';
import { ECLECTIC_GAME_ID, FREEZE_GAME_ID } from '@/features/game-definitions/tournamentOnly';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import appColors from '@/theme/colors';
import { card } from '../../typography';
import GameTile from '@/features/game-selection/GameTile';
import GroupedGameGrid from '@/features/game-selection/GroupedGameGrid';
import MeritDistributionBar from '@/features/game-selection/MeritDistributionBar';
import { cycleWeightPercent, normalizeWeight } from '@/features/game-selection/weights';
import { resolveSelectedRuleSetId } from '@/features/game-selection/ruleSets';
import SectionHeading from '../shared/SectionHeading';
import { TOUR_EDIT_MEDIA_PAGE_PATH } from '../../constants';
import type { GameDefinition } from '../../types';

type Props = {
  enabledGameDefinitions: GameDefinition[];
  tournamentGameIds: string[];
  weightsByGameId: Record<string, number>;
  ruleSetByGameId?: Record<string, string>;
  playerCount: number;
  roundCount?: number;
  onToggleGame: (gameIds: string[]) => void;
  onWeightChange: (gameId: string, weightPercent: number) => void;
  onRuleSetChange?: (gameId: string, ruleSetId: string) => void;
};

const FALLBACK_MERIT_PLAYER_COUNT = 4;

export default function TourGamesEditor({
  enabledGameDefinitions,
  tournamentGameIds,
  weightsByGameId,
  ruleSetByGameId,
  playerCount,
  roundCount = 1,
  onToggleGame,
  onWeightChange,
  onRuleSetChange,
}: Props) {
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const { user } = useUser();
  const [infoOpen, setInfoOpen] = useState(false);
  const [infoGame, setInfoGame] = useState<GameDefinition | null>(null);

  const tournamentSet = useMemo(() => new Set(tournamentGameIds), [tournamentGameIds]);
  const meritPlayerCount = playerCount > 1 ? playerCount : FALLBACK_MERIT_PLAYER_COUNT;
  const scoringPointsByGameId = useGameScoringRules(apiBaseUrl, user?.token, meritPlayerCount);

  const getWeightPercent = useCallback(
    (game: GameDefinition) => weightsByGameId[game.id] ?? normalizeWeight(game.defaultWeightPercent),
    [weightsByGameId],
  );

  const cycleWeight = useCallback(
    (game: GameDefinition) => {
      onWeightChange(game.id, cycleWeightPercent(getWeightPercent(game)));
    },
    [getWeightPercent, onWeightChange],
  );

  // The tour-long games (Freeze, Eclectic) pay their ladder × rounds^(2/3), so their tile
  // and legend show the tour total — what the winner actually collects. The exponent lives in
  // merits.ts and mirrors TourGameResultsProjector; changing it in one place only would make
  // the picker advertise a payout the tour never pays.
  const getTourLadder = useCallback(
    (game: GameDefinition) => {
      const id = game.id.toLowerCase();
      const isTourLong = id === FREEZE_GAME_ID || id === ECLECTIC_GAME_ID;
      // The round multiplier goes into getDisplayMeritLadder rather than onto its result:
      // merits round once, so scaling an already-rounded ladder would pay a different number.
      return getDisplayMeritLadder(
        scoringPointsByGameId,
        game.id,
        getWeightPercent(game),
        game.name,
        game.gameType,
        isTourLong ? roundCount : 1,
      );
    },
    [getWeightPercent, roundCount, scoringPointsByGameId],
  );

  const meritDistributionEntries = useMemo(
    () =>
      enabledGameDefinitions
        .filter((game) => tournamentSet.has(game.id))
        // Achievement and hazard games pay per unpredictable occurrence, so their
        // tour merits can't be projected. Per-event games (Mystery) can: one event
        // per player per round.
        .filter(
          (game) =>
            !isAchievementMeritGame(game.name, game.gameType) &&
            !isHazardMeritGame(game.name, game.gameType),
        )
        .map((game) => {
          const id = game.id.toLowerCase();
          return {
            gameId: game.id,
            name: getGameDisplayName(game),
            ladder: getTourLadder(game),
            // The Freeze/Eclectic ladders above are already the tour total;
            // regular games pay every round.
            paysPerRound: id !== ECLECTIC_GAME_ID && id !== FREEZE_GAME_ID,
            paysPerEvent: isPerEventMeritGame(game.gameType),
          };
        }),
    [enabledGameDefinitions, getTourLadder, tournamentSet],
  );

  const handleToggle = useCallback(
    (gameId: string) => {
      if (tournamentSet.has(gameId)) {
        onToggleGame(tournamentGameIds.filter((id) => id !== gameId));
      } else {
        onToggleGame([...tournamentGameIds, gameId]);
      }
    },
    [onToggleGame, tournamentGameIds, tournamentSet],
  );

  const openInfo = useCallback((game: GameDefinition) => {
    setInfoGame(game);
    setInfoOpen(true);
  }, []);

  const renderTile = useCallback(
    (game: GameDefinition) => {
      const selected = tournamentSet.has(game.id);
      const ladder = selected ? getTourLadder(game) : [];
      return (
        <GameTile
          key={game.id}
          game={game}
          selected={selected}
          mediaPagePath={TOUR_EDIT_MEDIA_PAGE_PATH}
          onPress={() => handleToggle(game.id)}
          onInfoPress={() => openInfo(game)}
          meritLadderLabel={ladder.length > 0 ? formatMeritLadder(ladder) : undefined}
          perTeam={(game.competitionType ?? '').toLowerCase() === 'team'}
          perAchievement={isAchievementMeritGame(game.name, game.gameType)}
          perEvent={isPerEventMeritGame(game.gameType)}
          weightPercent={getWeightPercent(game)}
          onWeightPress={() => cycleWeight(game)}
        />
      );
    },
    [cycleWeight, getTourLadder, getWeightPercent, handleToggle, openInfo, tournamentSet],
  );

  return (
    <View style={styles.container}>
      <SectionHeading title="Tour Games" count={tournamentGameIds.length} />
      <MeritDistributionBar entries={meritDistributionEntries} roundCount={roundCount} playerCount={meritPlayerCount} />
      {enabledGameDefinitions.length === 0 ? (
        <Text style={styles.emptyText}>No games available</Text>
      ) : (
        <GroupedGameGrid games={enabledGameDefinitions} renderTile={renderTile} />
      )}
      <RoundGameInfoModal
        visible={infoOpen}
        game={infoGame}
        apiBaseUrl={apiBaseUrl}
        onClose={() => setInfoOpen(false)}
        selectedRuleSetId={
          infoGame
            ? resolveSelectedRuleSetId({
                ruleSets: infoGame.rules,
                roundRuleSetId: ruleSetByGameId?.[infoGame.id],
              })
            : null
        }
        onSelectRuleSet={
          infoGame && onRuleSetChange ? (ruleSetId) => onRuleSetChange(infoGame.id, ruleSetId) : undefined
        }
        ruleScope="tour"
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    ...card,
    gap: 8,
  },
  emptyText: {
    fontSize: 14,
    color: appColors.text.muted,
    fontStyle: 'italic',
    paddingVertical: 12,
  },
});
